import { Chip } from '@mui/material';
import { format } from 'date-fns';

// Country code to flag emoji mapping
export const countryCodeToFlag = (countryCode: string): string => {
  const countryFlags: Record<string, string> = {
    DE: '🇩🇪',
    AT: '🇦🇹',
    CH: '🇨🇭',
    FR: '🇫🇷',
    NL: '🇳🇱',
    IT: '🇮🇹',
    GB: '🇬🇧',
    US: '🇺🇸',
    CA: '🇨🇦',
    ES: '🇪🇸',
    // Add more country mappings as needed
  };

  return countryFlags[countryCode] || countryCode;
};

// FTE chip color and styling logic
export const getFteChipProps = (fte: number | null | undefined) => {
  if (!fte && fte !== 0) {
    return {
      label: 'n/a',
      color: 'default' as const,
      variant: 'outlined' as const,
    };
  }

  if (fte < 15) {
    return {
      label: `${fte}`,
      color: 'info' as const,
      sx: { fontWeight: 500 },
    };
  } else if (fte >= 15 && fte <= 75) {
    return {
      label: `${fte}`,
      color: 'success' as const,
      sx: { fontWeight: 500 },
    };
  } else {
    return {
      label: `${fte}`,
      color: 'warning' as const,
      sx: { fontWeight: 500 },
    };
  }
};

// Format date without time
export const formatDateOnly = (dateString: string | Date): string => {
  if (!dateString) return '';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    return '';
  }
};

// Format callability score as percentage with color coding
export const getCallabilityChipProps = (score: number | null | undefined) => {
  if (score == null) {
    return {
      label: 'n/a',
      color: 'default' as const,
      variant: 'outlined' as const,
    };
  }

  const pct = Math.round(score * 100);

  if (pct >= 70) {
    return {
      label: `${pct}%`,
      color: 'success' as const,
      sx: { fontWeight: 500 },
    };
  } else if (pct >= 40) {
    return {
      label: `${pct}%`,
      color: 'warning' as const,
      sx: { fontWeight: 500 },
    };
  } else {
    return {
      label: `${pct}%`,
      color: 'default' as const,
      sx: { fontWeight: 500 },
    };
  }
};

// Get company logo placeholder or actual logo
export const getCompanyLogo = (logoUrl?: string, companyName?: string): React.ReactElement => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={companyName || 'Company logo'}
        style={{
          width: 24,
          height: 24,
          objectFit: 'contain',
          borderRadius: 4,
          pointerEvents: 'none',
        }}
        onError={e => {
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const placeholder = document.createElement('div');
          placeholder.style.width = '24px';
          placeholder.style.height = '24px';
          placeholder.style.borderRadius = '4px';
          placeholder.style.backgroundColor = 'rgba(128,128,128,0.15)';
          placeholder.style.display = 'flex';
          placeholder.style.alignItems = 'center';
          placeholder.style.justifyContent = 'center';
          placeholder.style.fontSize = '12px';
          placeholder.style.fontWeight = '600';
          placeholder.style.color = 'inherit';
          placeholder.style.opacity = '0.6';
          placeholder.style.pointerEvents = 'none';
          placeholder.textContent = companyName ? companyName.charAt(0).toUpperCase() : '?';
          target.parentNode?.replaceChild(placeholder, target);
        }}
      />
    );
  }

  // Placeholder logo
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: 4,
        backgroundColor: 'rgba(128,128,128,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        color: 'inherit',
        opacity: 0.6,
        pointerEvents: 'none',
      }}
    >
      {companyName ? companyName.charAt(0).toUpperCase() : '?'}
    </div>
  );
};
